import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner, ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import { useNetcheckApi } from "../api/netcheckApi";
import { netcheckConnect } from "../lib/connect";
import { formatRate } from "../lib/measure";
import { runNetcheck } from "../lib/runner";
import type { NetcheckResult, StepKey, StepState, Steps, Verdict } from "../types";
import "../../../styles/teacher.css";
import "../../../styles/netcheck.css";

const STEPS: readonly StepKey[] = ["https", "wss", "rtt", "down", "up"];
const IDLE: Steps = {
  https: "waiting",
  wss: "waiting",
  rtt: "waiting",
  down: "waiting",
  up: "waiting",
};
const STAGE_CLASS: Record<StepState, string> = {
  waiting: "pf-stage--unchecked",
  active: "pf-stage--hint",
  done: "pf-stage--done",
  warn: "pf-stage--hint",
  fail: "pf-stage--fail",
  skip: "pf-stage--skip",
};
const MARK: Record<StepState, string> = {
  waiting: "",
  active: "⏳ ",
  done: "✓ ",
  warn: "△ ",
  fail: "✕ ",
  skip: "— ",
};
const KPI_CLASS: Record<Verdict, string> = {
  pass: "pf-kpi pf-netcheck__kpi pf-netcheck__kpi--pass",
  warn: "pf-kpi pf-kpi--warn pf-netcheck__kpi pf-netcheck__kpi--warn",
  fail: "pf-kpi pf-netcheck__kpi pf-netcheck__kpi--fail",
};

/**
 * FS-C1-08 학교망 진단(교사 · PH2 · SU/공통) — SR-NET-05 · RSK-04 · ARC §14 '네트워크 체크' · TC-NET-05 — /teacher/netcheck.
 * SCR 에 이 화면의 와이어가 없어 FS-C1 공통 상태·오류 부록(v0.6 sfs-c1)의 틀로 짰다: 단계 칩(.stage — FS-C1-05 전환 단계와 같은 모양)
 * · 지표 타일(.kpi) · 판정 배너(교사 3요소: 원인 · 다음 행동 · 문의 코드) · '막다른 화면 금지'(막히면 혼자 놀이터 안내 · 다시 진단).
 *  - 단계: HTTPS 연결(제어 평면 핑) → WSS 연결(룸 서버 공개 주소 — 룸과 같은 길) → 왕복 시간 → 내려받기 → 올리기
 *  - 타일 4: 실시간 연결(WSS) · 왕복 시간(중앙값 · 90%) · 내려받기 · 올리기 — 기준(서버 thresholds)과 함께 · 주의 노랑 · 어려움 빨강
 *  - 포트 줄: HTTPS · WSS 둘 다 443 이면 '단일 포트 ✓' · 아니면 실제 포트(개발 · 시험 환경)
 *  - 판정: 통과(ok 배너) · 주의(warn — NET-SLOW) · 어려움(err — FE-303 변형: WSS 막힘이면 '혼자 놀이터 사용 가능 ✓' 칩)
 *  - 전산 담당 안내(허용 주소 · WebSocket 업그레이드 · SSL 검사 예외) + 복사
 * 결과는 제어 평면이 같은 기준으로 다시 매겨 기관 단위 · 비식별로 모은다(SR-NET-05 규칙).
 */
export function NetcheckScreen() {
  const t = useT();
  const api = useNetcheckApi();
  const [steps, setSteps] = useState<Steps>(IDLE);
  const [result, setResult] = useState<NetcheckResult | null>(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const abort = useRef<AbortController | null>(null);

  useTopBar({
    icon: "📶",
    title: t("netcheck.topTitle"),
    chips: [
      { label: t("netcheck.chip443"), testId: "netcheck-chip-443" },
      { label: t("netcheck.chipAnon") },
    ],
  });

  useEffect(() => () => abort.current?.abort(), []);

  const start = useCallback(async () => {
    if (!api || running) return;
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    setRunning(true);
    setResult(null);
    setCopied(false);
    setSteps(IDLE);
    try {
      const r = await runNetcheck({
        api,
        connect: netcheckConnect,
        pageUrl: window.location.href,
        signal: ac.signal,
        onStep: (k, s) => !ac.signal.aborted && setSteps((cur) => ({ ...cur, [k]: s })),
      });
      if (!ac.signal.aborted) setResult(r);
    } catch {
      /* 화면을 떠났다 */
    } finally {
      if (!ac.signal.aborted) setRunning(false);
    }
  }, [api, running]);

  const adminText = result
    ? [
        t("netcheck.adminHost", { host: result.wsHost ?? window.location.host }),
        t("netcheck.adminProxy"),
        t("netcheck.adminSsl"),
      ]
    : [];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(adminText.join("\n"));
      setCopied(true);
    } catch {
      /* 클립보드 권한 없음 — 글은 화면에 있다 */
    }
  };

  return (
    <ScreenRoot
      id="FS-C1-08"
      title={t("netcheck.title")}
      hideTitle
      className="pf-teacher pf-netcheck"
    >
      <section className="pf-pane" aria-labelledby="nc-intro">
        <div className="pf-row pf-row--wrap pf-netcheck__head">
          <div className="pf-netcheck__lead">
            <h2 id="nc-intro" className="pf-h3">
              {t("netcheck.introTitle")}
            </h2>
            <p className="pf-muted">{t("netcheck.intro")}</p>
          </div>
          <Button
            variant="primary"
            icon={result ? "↻" : "▶"}
            onClick={() => void start()}
            disabled={!api || running}
            aria-busy={running}
            data-testid="netcheck-start"
          >
            {running ? t("netcheck.running") : result ? t("netcheck.again") : t("netcheck.start")}
          </Button>
        </div>
        <ol
          className="pf-stages pf-netcheck__steps"
          aria-label={t("netcheck.stepsLabel")}
          data-testid="netcheck-steps"
        >
          {STEPS.map((k) => (
            <li
              key={k}
              className={`pf-stage ${STAGE_CLASS[steps[k]]}`}
              data-step={k}
              data-state={steps[k]}
              aria-current={steps[k] === "active" ? "step" : undefined}
            >
              {MARK[steps[k]]}
              {t(`netcheck.step.${k}`)}
            </li>
          ))}
        </ol>
      </section>

      {result && <Verdictbanner result={result} />}

      {result && (
        <div className="pf-kpis pf-netcheck__kpis" data-testid="netcheck-kpis">
          <div
            className={KPI_CLASS[result.wss === "ok" ? "pass" : "fail"]}
            data-kpi="wss"
            data-level={result.wss === "ok" ? "pass" : "fail"}
          >
            <span className="pf-muted">{t("netcheck.kpi.wss")}</span>
            <b>
              {t(
                result.wss === "ok"
                  ? "netcheck.kpi.wssOk"
                  : result.wss === "timeout"
                    ? "netcheck.kpi.wssTimeout"
                    : "netcheck.kpi.wssBlocked",
              )}
            </b>
            {result.httpsRttMs != null && (
              <span className="pf-muted pf-netcheck__need">
                {t("netcheck.httpsRtt", { ms: Math.round(result.httpsRttMs) })}
              </span>
            )}
          </div>
          <Kpi
            k="rtt"
            label={t("netcheck.kpi.rtt")}
            value={
              result.rttMs == null
                ? null
                : t("netcheck.kpi.rttValue", { ms: Math.round(result.rttMs) })
            }
            sub={
              result.rttP90Ms == null
                ? t("netcheck.kpi.needRtt", { v: 150 })
                : `${t("netcheck.kpi.rttP90", { ms: Math.round(result.rttP90Ms) })} · ${t("netcheck.kpi.needRtt", { v: 150 })}`
            }
            level={result.wss === "ok" ? result.levels.rtt : null}
          />
          <Kpi
            k="down"
            label={t("netcheck.kpi.down")}
            value={result.downKbps == null ? null : formatRate(result.downKbps)}
            sub={t("netcheck.kpi.need", { v: formatRate(2500) })}
            level={result.wss === "ok" ? result.levels.down : null}
          />
          <Kpi
            k="up"
            label={t("netcheck.kpi.up")}
            value={result.upKbps == null ? null : formatRate(result.upKbps)}
            sub={t("netcheck.kpi.need", { v: formatRate(240) })}
            level={result.wss === "ok" ? result.levels.up : null}
          />
        </div>
      )}

      {result && (
        <section className="pf-pane" aria-labelledby="nc-ports" data-testid="netcheck-ports">
          <h2 id="nc-ports" className="pf-h3">
            {t("netcheck.portsTitle")}
          </h2>
          <p data-single={result.port443 ? "" : undefined}>
            <span
              className={`pf-chip-static${result.port443 ? " pf-chip-static--ok" : " pf-chip-static--info"}`}
            >
              {result.port443
                ? t("netcheck.ports443")
                : result.wssPort == null
                  ? t("netcheck.portsNone", { https: result.httpsPort })
                  : t("netcheck.portsOther", { https: result.httpsPort, wss: result.wssPort })}
            </span>
          </p>
        </section>
      )}

      {result && result.verdict !== "pass" && (
        <section className="pf-pane" aria-labelledby="nc-admin" data-testid="netcheck-admin">
          <div className="pf-row pf-row--wrap pf-netcheck__head">
            <h2 id="nc-admin" className="pf-h3">
              {t("netcheck.adminTitle")}
            </h2>
            <Button variant="ghost" icon="⧉" className="pf-btn--sm" onClick={() => void copy()}>
              {copied ? t("netcheck.copied") : t("netcheck.copy")}
            </Button>
          </div>
          <ul className="pf-netcheck__admin">
            {adminText.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
      )}
      <p className="pf-muted pf-netcheck__privacy">{t("netcheck.privacy")}</p>
    </ScreenRoot>
  );
}

function Kpi({
  k,
  label,
  value,
  sub,
  level,
}: {
  k: string;
  label: string;
  value: string | null;
  sub: string;
  level: Verdict | null;
}) {
  const t = useT();
  return (
    <div
      className={level ? KPI_CLASS[level] : "pf-kpi pf-kpi--dim"}
      data-kpi={k}
      data-level={level ?? "none"}
      aria-disabled={level ? undefined : "true"}
    >
      <span className="pf-muted">{label}</span>
      <b>{value ?? t("netcheck.kpi.notMeasured")}</b>
      <span className="pf-muted pf-netcheck__need">{sub}</span>
    </div>
  );
}

/** 판정 배너 — 통과 ok · 주의 warn(NET-SLOW) · 어려움 err(FE-303 변형 — 혼자 놀이터 안내) */
function Verdictbanner({ result }: { result: NetcheckResult }) {
  const t = useT();
  if (result.verdict === "pass")
    return (
      <div
        className="pf-banner pf-banner--ok"
        role="status"
        data-testid="netcheck-verdict"
        data-verdict="pass"
      >
        <p className="pf-banner__text">
          <b className="pf-banner__cause">✅ {t("netcheck.verdict.pass")}</b>{" "}
          <span className="pf-banner__next">{t("netcheck.verdict.passNext")}</span>
        </p>
      </div>
    );
  if (result.verdict === "warn")
    return (
      <div
        className="pf-banner pf-banner--warn"
        role="status"
        data-testid="netcheck-verdict"
        data-verdict="warn"
        data-fe-code="NET-SLOW"
      >
        <p className="pf-banner__text">
          <b className="pf-banner__cause">💡 {t("netcheck.verdict.warnCause")}</b>{" "}
          <span className="pf-banner__next">{t("netcheck.verdict.warnNext")}</span>{" "}
          <code className="pf-banner__code">({t("common.codeLabel", { code: "NET-SLOW" })})</code>
        </p>
      </div>
    );
  return (
    <div data-testid="netcheck-verdict" data-verdict="fail">
      <ErrorBanner
        cause={t(
          result.wss === "ok" ? "netcheck.verdict.failCause" : "netcheck.verdict.blockedCause",
        )}
        action={t("netcheck.verdict.failNext")}
        code="FE-303"
      />
      <p className="pf-netcheck__async">
        <span className="pf-chip-static pf-chip-static--ok" data-testid="netcheck-async">
          {t("netcheck.verdict.asyncOk")}
        </span>
      </p>
    </div>
  );
}
